import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export const GENERATION_LOADING_MESSAGES = [
  'Analizando el currículum...',
  'Diseñando la estructura pedagógica...',
  'Trazando elementos geométricos...',
  'Ajustando el diseño editorial...',
  'Casi listo, afinando detalles...',
] as const;

const MESSAGE_INTERVAL_MS = 3500;
export const GENERATION_OVERLAY_EXIT_MS = 500;

export interface GenerationLoadingOverlayProps {
  visible: boolean;
  messages?: readonly string[];
  /** Se invoca tras completar el fade-out (500ms). */
  onExitComplete?: () => void;
}

export function GenerationLoadingOverlay({
  visible,
  messages = GENERATION_LOADING_MESSAGES,
  onExitComplete,
}: GenerationLoadingOverlayProps) {
  const [messageIndex, setMessageIndex] = useState(0);
  const [shouldRender, setShouldRender] = useState(visible);
  const [isExiting, setIsExiting] = useState(false);
  const shouldRenderRef = useRef(visible);

  useEffect(() => {
    if (visible) {
      shouldRenderRef.current = true;
      setShouldRender(true);
      setIsExiting(false);
      return undefined;
    }

    if (!shouldRenderRef.current) return undefined;

    setIsExiting(true);
    const exitTimer = window.setTimeout(() => {
      shouldRenderRef.current = false;
      setShouldRender(false);
      setIsExiting(false);
      onExitComplete?.();
    }, GENERATION_OVERLAY_EXIT_MS);

    return () => window.clearTimeout(exitTimer);
  }, [visible, onExitComplete]);

  useEffect(() => {
    if (!shouldRender) {
      setMessageIndex(0);
      return;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const intervalId = window.setInterval(() => {
      setMessageIndex((current) => (current + 1) % messages.length);
    }, MESSAGE_INTERVAL_MS);

    return () => {
      window.clearInterval(intervalId);
      document.body.style.overflow = previousOverflow;
    };
  }, [shouldRender, messages]);

  if (!shouldRender || typeof document === 'undefined') {
    return null;
  }

  const message = messages[messageIndex] ?? messages[0];

  return createPortal(
    <div
      className={[
        'generation-loading-overlay will-change-[opacity,transform]',
        isExiting ? 'generation-loading-overlay--exit' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      role="alertdialog"
      aria-modal="true"
      aria-busy={!isExiting}
      aria-live="polite"
      aria-label="Generando guía con inteligencia artificial"
    >
      <div
        className={[
          'generation-loading-overlay__content',
          isExiting ? 'generation-loading-overlay__content--exit' : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <div className="generation-loading-overlay__stage" aria-hidden="true">
          <div className="generation-loading-overlay__orbit" />
          <div className="generation-loading-overlay__morph" />
          <div className="generation-loading-overlay__accent" />
        </div>

        <p key={messageIndex} className="generation-loading-overlay__message">
          {message}
        </p>
      </div>
    </div>,
    document.body,
  );
}
